<template>
  <NavBar />

  <!-- Fulfill Goods Order Dialog -->
  <FulfillGoodsOrderDialog
    v-model="showFulfillDialog"
    :order="selectedGoodsOrder"
    @orderFulfilled="onOrderFulfilled"
  />

  <!-- Confirm Payment Dialog -->
  <v-dialog v-model="showPaymentDialog" max-width="500" persistent>
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon class="mr-2">mdi-cash-check</v-icon>
        {{ t.orders.confirm_payment_title }}
      </v-card-title>
      <v-card-text class="pa-4">
        <div class="mb-3">
          <strong>{{ selectedPendingInvoiceOrders.length }}</strong> {{ t.orders.orders }}
          {{ t.orders.pending_invoice || 'pending_invoice' }}
          <span class="ml-2 font-weight-bold">
            {{ formatCurrency(selectedPendingInvoiceTotal) }}
          </span>
        </div>
        <v-text-field
          v-model.number="paymentAmount"
          :label="t.orders.payment_amount"
          :hint="t.orders.payment_amount_hint"
          persistent-hint
          type="number"
          step="0.01"
          min="0"
          density="compact"
          prepend-inner-icon="mdi-currency-eur"
          class="mb-3"
        ></v-text-field>
        <v-textarea
          v-model="paymentNotes"
          :label="t.orders.payment_notes"
          rows="2"
          density="compact"
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn variant="text" @click="showPaymentDialog = false">{{ t.common.cancel }}</v-btn>
        <v-btn
          color="success"
          variant="flat"
          @click="submitConfirmPayment"
          :loading="paymentLoading"
        >
          <v-icon class="mr-1">mdi-check</v-icon>
          {{ t.orders.confirm_payment }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <!-- Payment Result Snackbar -->
  <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="5000" location="top">
    {{ snackbarMessage }}
    <template v-slot:actions>
      <v-btn variant="text" @click="showSnackbar = false">OK</v-btn>
    </template>
  </v-snackbar>

  <v-container fluid style="margin-top: 185px;">
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon class="mr-2">mdi-clipboard-list</v-icon>
        {{ t.orders.title }}
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Search Filters - Row 1: Client selection with scope -->
        <v-row align="center" class="mb-2">
          <!-- Client Selection -->
          <v-col cols="12" md="3">
            <ClientHierarchySelect
              v-model="selectedClientId"
              :clients-list="clientsList"
              :label="t.orders?.client || 'Client'"
              placeholder="Toate"
              clearable
              auto-select-own
              @change="onClientChange"
            />
          </v-col>

          <!-- Client Scope Radio -->
          <v-col cols="12" md="2" class="pt-3">
            <v-radio-group v-model="includeSubclients" inline density="compact" hide-details class="mt-0">
              <v-radio :label="t.users.own" :value="false"></v-radio>
              <v-radio :label="t.users.include_subclients" :value="true"></v-radio>
            </v-radio-group>
          </v-col>

          <!-- IMSI/Serial Search -->
          <v-col cols="12" md="2" class="pt-3">
            <v-text-field
              v-model="searchImsi"
              :label="t.orders.search_imsi_serial"
              prepend-inner-icon="mdi-sim"
              clearable
              density="compact"
              hide-details
              @keyup.enter="loadOrders"
            ></v-text-field>
          </v-col>

          <!-- MSISDN Search -->
          <v-col cols="12" md="2" class="pt-3">
            <v-text-field
              v-model="searchMsisdn"
              :label="t.orders.search_msisdn"
              prepend-inner-icon="mdi-phone"
              clearable
              density="compact"
              hide-details
              @keyup.enter="loadOrders"
            ></v-text-field>
          </v-col>

          <!-- Order Status Filter -->
          <v-col cols="12" md="1" class="pt-3">
            <v-select
              v-model="selectedStatusId"
              :items="orderStatusOptions"
              item-value="id"
              item-title="name"
              :label="t.common.status"
              density="compact"
              hide-details
            ></v-select>
          </v-col>

          <!-- Search Button -->
          <v-col cols="12" md="2" class="d-flex align-center ga-2 pt-5">
            <v-btn color="primary" @click="pagination.page = 1; loadOrders()" :loading="loading">
              <v-icon class="mr-1">mdi-magnify</v-icon>
              {{ t.common.search }}
            </v-btn>
          </v-col>
        </v-row>

        <!-- Service Type Filter -->
        <v-row align="center" class="mb-1">
          <v-col cols="12">
            <ServiceTypeButtonFilter v-model="serviceTypeFilter" />
          </v-col>
        </v-row>

        <!-- Search Filters - Row 2: Period (radio buttons) -->
        <v-row align="center" class="mb-1">
          <v-col cols="12" md="auto" class="d-flex align-center">
            <v-icon size="small" class="mr-2 text-grey">mdi-calendar-range</v-icon>
            <v-btn-toggle v-model="selectedPeriod" mandatory density="compact" color="primary" variant="outlined">
              <v-btn v-for="opt in periodOptions" :key="opt.value" :value="opt.value" size="small">
                {{ opt.label }}
              </v-btn>
            </v-btn-toggle>
            <template v-if="selectedPeriod === 'custom'">
              <v-text-field
                v-model="customPeriodStart"
                type="date"
                density="compact"
                hide-details
                variant="outlined"
                class="ml-3"
                style="max-width: 160px"
              ></v-text-field>
              <span class="mx-1">-</span>
              <v-text-field
                v-model="customPeriodEnd"
                type="date"
                density="compact"
                hide-details
                variant="outlined"
                style="max-width: 160px"
              ></v-text-field>
            </template>
          </v-col>
          <v-col cols="12" md="auto">
            <v-btn
              color="success"
              variant="outlined"
              size="small"
              @click="exportExcel"
              :loading="exportLoading"
            >
              <v-icon class="mr-1">mdi-file-excel</v-icon>
              Export Excel
            </v-btn>
          </v-col>

          <!-- Summary switches -->
          <v-col cols="12" md="auto" class="d-flex align-center ga-3">
            <v-switch
              v-model="showSummaryTotals"
              :label="t.orders.summary_totals_only"
              density="compact"
              hide-details
              color="primary"
              class="mt-0"
            ></v-switch>
            <v-switch
              v-model="showSummaryDetailed"
              :label="t.orders.summary_detailed"
              density="compact"
              hide-details
              color="primary"
              class="mt-0"
              :disabled="!showSummaryTotals"
            ></v-switch>
          </v-col>

          <v-spacer></v-spacer>

          <!-- Batch Action Buttons -->
          <v-col cols="12" md="auto" class="d-flex ga-2">
            <!-- Confirm Payment (admin/financial only, for sub-clients) -->
            <v-btn
              v-if="canConfirmPayment"
              color="success"
              variant="flat"
              size="small"
              @click="openPaymentDialog"
              :disabled="selectedPendingInvoiceOrders.length === 0"
            >
              <v-icon class="mr-1">mdi-cash-check</v-icon>
              {{ t.orders.confirm_payment }}
              <v-chip v-if="selectedPendingInvoiceOrders.length > 0" class="ml-2" size="x-small" color="white">
                {{ selectedPendingInvoiceOrders.length }}
              </v-chip>
            </v-btn>

            <!-- Pay Online (for own orders) -->
            <v-btn
              v-if="selectedPendingInvoiceOrders.length > 0"
              color="primary"
              variant="flat"
              size="small"
              @click="payOnline"
              :loading="payOnlineLoading"
            >
              <v-icon class="mr-1">mdi-credit-card-outline</v-icon>
              {{ t.orders.pay_online }}
              <span class="ml-1">({{ formatCurrency(selectedPendingInvoiceTotal) }})</span>
            </v-btn>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Summary Section -->
        <v-card
          v-if="summary && showSummaryTotals"
          variant="outlined"
          color="blue"
          class="mb-4 summary-card"
        >
          <v-card-title class="text-subtitle-1 d-flex align-center py-2">
            <v-icon size="small" class="mr-2">mdi-chart-box</v-icon>
            {{ t.orders.summary }}
          </v-card-title>
          <v-card-text class="pa-3">
            <!-- Grand Totals -->
            <v-row dense>
              <v-col cols="12" md="2">
                <div class="text-caption font-weight-medium text-medium-emphasis">{{ t.orders.grand_total }}</div>
                <div class="text-h6 font-weight-bold">{{ formatCurrency(summary.grand_total) }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption font-weight-medium text-medium-emphasis">{{ t.orders.total_paid }}</div>
                <div class="text-h6 text-success font-weight-bold">{{ formatCurrency(summary.grand_total_paid) }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption font-weight-medium text-medium-emphasis">{{ t.orders.total_unpaid }}</div>
                <div class="text-h6 text-error font-weight-bold">{{ formatCurrency(summary.grand_total_unpaid) }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption font-weight-medium text-medium-emphasis">{{ t.orders.orders }}</div>
                <div class="text-h6 font-weight-bold">{{ summary.total_orders }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption font-weight-medium text-medium-emphasis">{{ t.orders.paid_orders }}</div>
                <div class="text-h6 text-success font-weight-bold">{{ summary.total_paid_orders }}</div>
              </v-col>
              <v-col cols="12" md="2">
                <div class="text-caption font-weight-medium text-medium-emphasis">{{ t.orders.unpaid_orders }}</div>
                <div class="text-h6 text-error font-weight-bold">{{ summary.total_unpaid_orders }}</div>
              </v-col>
            </v-row>

            <!-- Detailed Breakdown (by_client) -->
            <div v-if="showSummaryDetailed && summary.by_client && summary.by_client.length > 0" class="mt-4">
              <v-divider class="mb-3"></v-divider>
              <div v-for="client in summary.by_client" :key="client.client_id" class="mb-3 pa-2 rounded detailed-client-row">
                <div class="d-flex align-center mb-1">
                  <v-icon size="small" class="mr-1" color="primary">mdi-domain</v-icon>
                  <strong class="text-body-1">{{ client.client_name }}</strong>
                  <v-chip size="x-small" class="ml-2" color="primary" variant="flat">
                    {{ client.order_count }} {{ t.orders.order_count }}
                  </v-chip>
                  <span class="ml-2 font-weight-bold text-body-1">{{ formatCurrency(client.total_cost) }}</span>
                </div>

                <!-- By Service Type -->
                <div v-if="client.by_service_type" class="ml-6">
                  <div v-for="svc in client.by_service_type" :key="svc.service_type + svc.service_name" class="d-flex align-center text-body-2 py-1">
                    <v-chip size="x-small" color="info" variant="flat" class="mr-2">{{ svc.service_type }}</v-chip>
                    <span class="mr-2 font-weight-medium">{{ svc.service_name }}</span>
                    <span class="text-medium-emphasis font-weight-bold mr-2">x{{ svc.count }}</span>
                    <span class="font-weight-bold">{{ formatCurrency(svc.total_cost) }}</span>

                    <!-- By IMSI (expandable) -->
                    <template v-if="svc.by_imsi && svc.by_imsi.length > 0">
                      <v-menu>
                        <template v-slot:activator="{ props }">
                          <v-btn v-bind="props" variant="text" size="x-small" class="ml-1">
                            <v-icon size="small">mdi-sim</v-icon>
                            {{ svc.by_imsi.length }}
                          </v-btn>
                        </template>
                        <v-list density="compact">
                          <v-list-item v-for="imsi in svc.by_imsi" :key="imsi.imsi_serial">
                            <v-list-item-title>
                              <code>{{ imsi.imsi_serial }}</code>
                              <span class="ml-2 text-medium-emphasis font-weight-bold">x{{ imsi.count }}</span>
                              <span class="ml-2 font-weight-medium">{{ formatCurrency(imsi.total_cost) }}</span>
                            </v-list-item-title>
                          </v-list-item>
                        </v-list>
                      </v-menu>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </v-card-text>
        </v-card>

        <!-- GOODS ORDERS SECTION (For Distributors) -->
        <v-card
          v-if="isDistributor && pendingGoodsOrders.length > 0"
          variant="tonal"
          color="warning"
          class="mb-4"
        >
          <v-card-title class="d-flex align-center">
            <v-icon start>mdi-package-variant-closed</v-icon>
            Comenzi Bunuri de Procesat
            <v-chip class="ml-2" color="error" size="small">
              {{ pendingGoodsOrders.length }}
            </v-chip>
          </v-card-title>

          <v-card-text class="pa-0">
            <v-data-table
              :items="pendingGoodsOrders"
              :headers="goodsOrderHeaders"
              :loading="loadingGoodsOrders"
              density="compact"
              class="elevation-0"
              :items-per-page="10"
            >
              <!-- ID -->
              <template v-slot:[`item.id`]="{ item }">
                <span class="font-weight-bold">#{{ item.id }}</span>
              </template>

              <!-- Created At -->
              <template v-slot:[`item.created_at`]="{ item }">
                {{ formatDate(item.created_at) }}
              </template>

              <!-- Requester -->
              <template v-slot:[`item.requester`]="{ item }">
                <div class="font-weight-medium">{{ item.requester_client_name }}</div>
                <div class="text-caption text-grey">{{ item.requester_email }}</div>
              </template>

              <!-- Details -->
              <template v-slot:[`item.details`]="{ item }">
                <v-chip size="x-small" class="mr-1" color="info">{{ item.good_category_name }}</v-chip>
                {{ item.quantity }}x {{ item.product_code_name }}
                <div v-if="item.network_name" class="text-caption">Retea: {{ item.network_name }}</div>
              </template>

              <!-- Delivery -->
              <template v-slot:[`item.delivery`]="{ item }">
                <div>{{ item.delivery_type_name }}</div>
                <div class="text-caption">{{ item.delivery_cost }} {{ item.currency }}</div>
                <div class="text-caption text-grey text-truncate" style="max-width: 200px;">
                  {{ item.delivery_address_short || item.delivery_address_full }}
                </div>
              </template>

              <!-- Total -->
              <template v-slot:[`item.total`]="{ item }">
                <span class="font-weight-bold">{{ item.total_amount }} {{ item.currency }}</span>
              </template>

              <!-- Status -->
              <template v-slot:[`item.status`]="{ item }">
                <v-chip :color="getGoodsOrderStatusColor(item.order_status)" size="small">
                  {{ item.order_status_display || item.order_status }}
                </v-chip>
                <div v-if="item.order_status === 'pending_invoice'" class="text-caption text-grey">
                  (postpaid)
                </div>
              </template>

              <!-- Actions -->
              <template v-slot:[`item.actions`]="{ item }">
                <v-btn
                  v-if="canFulfillGoodsOrder(item)"
                  color="primary"
                  size="small"
                  @click="openFulfillDialog(item)"
                >
                  <v-icon start>mdi-truck-delivery</v-icon>
                  Livreaza
                </v-btn>
                <v-btn
                  v-else
                  variant="text"
                  size="small"
                  @click="openFulfillDialog(item)"
                >
                  <v-icon>mdi-eye</v-icon>
                </v-btn>
              </template>
            </v-data-table>
          </v-card-text>
        </v-card>

        <!-- Order Type Tabs (loaded from API) + Payment History -->
        <v-tabs v-model="orderTypeTab" class="mb-4" density="compact" @update:model-value="onOrderTypeChange">
          <v-tab value="all">{{ t.orders.period_all }}</v-tab>
          <v-tab v-for="ot in availableOrderTypes" :key="ot" :value="ot">
            {{ ot }}
          </v-tab>
          <v-tab value="payment_history">
            <v-icon size="small" class="mr-1">mdi-history</v-icon>
            {{ t.orders.payment_history }}
          </v-tab>
        </v-tabs>

        <!-- ============ PAYMENT HISTORY TAB ============ -->
        <template v-if="orderTypeTab === 'payment_history'">
          <div class="text-caption mb-2" v-if="!loadingBatches">
            {{ t.common.showing }} {{ paymentBatches.length }} {{ t.common.of }} {{ batchesPagination.total_count }} {{ t.orders.payment_history }}
          </div>

          <v-data-table
            :headers="batchHeaders"
            :items="paymentBatches"
            :loading="loadingBatches"
            :items-per-page="batchesPagination.per_page"
            density="compact"
            class="elevation-1"
            show-expand
            item-value="id"
          >
            <template v-slot:[`item.id`]="{ item }">
              <span class="font-weight-bold">#{{ item.id }}</span>
            </template>

            <template v-slot:[`item.created_at`]="{ item }">
              {{ formatDate(item.created_at) }}
            </template>

            <template v-slot:[`item.amount_received`]="{ item }">
              <span class="font-weight-bold">{{ formatCurrency(item.amount_received) }}</span>
            </template>

            <template v-slot:[`item.amount_applied`]="{ item }">
              <span class="text-success">{{ formatCurrency(item.amount_applied) }}</span>
            </template>

            <template v-slot:[`item.amount_credit`]="{ item }">
              <span v-if="parseFloat(item.amount_credit) > 0" class="text-info">{{ formatCurrency(item.amount_credit) }}</span>
              <span v-else class="text-grey">-</span>
            </template>

            <template v-slot:[`item.orders_count`]="{ item }">
              <v-chip size="x-small" color="success">{{ item.orders_paid_count }}</v-chip>
              <span class="text-grey mx-1">/</span>
              <v-chip size="x-small" color="grey">{{ item.orders_total_count }}</v-chip>
            </template>

            <template v-slot:[`item.payment_method`]="{ item }">
              <v-chip size="x-small" :color="item.payment_method === 'online' ? 'primary' : 'grey'" variant="outlined">
                {{ item.payment_method }}
              </v-chip>
            </template>

            <template v-slot:[`item.data-table-expand`]="{ toggleExpand, isExpanded, internalItem }">
              <v-btn size="x-small" :color="isExpanded(internalItem) ? 'primary' : 'info'" :variant="isExpanded(internalItem) ? 'flat' : 'elevated'" @click="toggleExpand(internalItem)">
                <v-icon size="small" class="mr-1">{{ isExpanded(internalItem) ? 'mdi-eye' : 'mdi-eye-outline' }}</v-icon>
                Info
              </v-btn>
            </template>

            <!-- Expanded Row: orders + CS details -->
            <template v-slot:expanded-row="{ columns, item }">
              <tr>
                <td :colspan="columns.length" class="pa-0">
                  <v-card flat class="ma-2 bg-grey-lighten-4">
                    <v-card-text class="pa-2">
                      <div v-if="item.orders && item.orders.length > 0">
                        <div v-for="order in item.orders" :key="order.order_id" class="mb-3">
                          <div class="d-flex align-center mb-1">
                            <v-icon size="small" class="mr-1">mdi-receipt</v-icon>
                            <strong>Order #{{ order.order_id }}</strong>
                            <v-chip size="x-small" class="ml-2" :color="order.paid ? 'success' : 'warning'">
                              {{ order.paid ? t.orders.paid : t.orders.unpaid }}
                            </v-chip>
                            <span class="ml-2">{{ formatCurrency(order.total_cost) }}</span>
                            <span class="ml-2 text-caption text-grey">{{ formatDate(order.created_at) }}</span>
                          </div>
                          <v-table density="compact" v-if="order.client_services && order.client_services.length > 0">
                            <thead>
                              <tr class="bg-grey-lighten-2">
                                <th>CS</th>
                                <th>{{ t.orders.service }}</th>
                                <th>{{ t.orders.service_type }}</th>
                                <th>{{ t.orders.client }}</th>
                                <th>{{ t.orders.cs_status }}</th>
                                <th>IMSI</th>
                                <th>Serial</th>
                                <th>MSISDN</th>
                                <th>{{ t.orders.seria_echipament }}</th>
                                <th>{{ t.orders.nui }}</th>
                                <th>{{ t.orders.technician }}</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr v-for="cs in order.client_services" :key="cs.cs_id">
                                <td><code class="text-primary">{{ cs.cs_id }}</code></td>
                                <td>{{ cs.service_name }}</td>
                                <td><v-chip size="x-small" color="info" variant="outlined">{{ cs.service_type }}</v-chip></td>
                                <td>{{ cs.client_name }}</td>
                                <td><v-chip size="x-small" :color="getCsStatusColor(cs.cs_status)">{{ cs.cs_status }}</v-chip></td>
                                <td><code v-if="cs.imsi">{{ cs.imsi }}</code><span v-else class="text-grey">-</span></td>
                                <td><code v-if="cs.imsi_serial">{{ cs.imsi_serial }}</code><span v-else class="text-grey">-</span></td>
                                <td><code v-if="cs.msisdn">{{ cs.msisdn }}</code><span v-else class="text-grey">-</span></td>
                                <td>{{ cs.seria_echipament || '-' }}</td>
                                <td>{{ cs.nui || '-' }}</td>
                                <td>{{ cs.technician || '-' }}</td>
                              </tr>
                            </tbody>
                          </v-table>
                        </div>
                      </div>
                      <div v-else class="text-center text-grey pa-4">
                        {{ t.orders.no_client_services }}
                      </div>
                    </v-card-text>
                  </v-card>
                </td>
              </tr>
            </template>
          </v-data-table>

          <v-pagination
            v-if="batchesPagination.total_pages > 1"
            v-model="batchesPagination.page"
            :length="batchesPagination.total_pages"
            :total-visible="7"
            class="mt-4"
            @update:model-value="loadPaymentHistory"
          ></v-pagination>
        </template>

        <!-- ============ ORDERS TAB (default) ============ -->
        <template v-else>

        <!-- Results Count -->
        <div class="text-caption mb-2" v-if="!loading">
          {{ t.common.showing }} {{ orders.length }} {{ t.common.of }} {{ pagination.total_count }} {{ t.orders.orders }}
        </div>


        <!-- Orders Table -->
        <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-0"></v-progress-linear>
        <table class="orders-table elevation-1">
          <thead>
            <tr>
              <th style="width:40px">
                <v-checkbox v-model="selectAll" hide-details density="compact" class="mt-0 pt-0" />
              </th>
              <th style="width:80px">ID</th>
              <th style="width:140px">{{ t.orders.created_at }}</th>
              <th>{{ t.orders?.client || 'Client' }}</th>
              <th style="width:120px">{{ t.common.status }}</th>
              <th style="width:100px">{{ t.orders.total_cost }}</th>
              <th style="width:80px">{{ t.orders.nr_sims }}</th>
              <th>{{ t.orders.services }}</th>
              <th style="width:250px">{{ t.common.actions }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!loading && orders.length === 0">
              <td colspan="9" class="text-center text-grey pa-4">{{ t.orders.no_orders || 'No orders found' }}</td>
            </tr>
            <tr v-for="item in orders" :key="item.id">
              <td>
                <v-checkbox v-model="selectedOrders" :value="item.id" hide-details density="compact" class="mt-0 pt-0" />
              </td>
              <td>#{{ item.id }}</td>
              <td>{{ formatDate(item.created_at) }}</td>
              <td>{{ item.client_name || '-' }}</td>
              <td><v-chip size="small" :color="getStatusColor(item.order_status_name)">{{ item.order_status_name }}</v-chip></td>
              <td>{{ item.total_cost ? formatCurrency(item.total_cost, item.currency) : '-' }}</td>
              <td>{{ item.nr_of_sims || 0 }}</td>
              <td>{{ item.order_client_services ? item.order_client_services.length + ' CS' : '-' }}</td>
              <td>
                <v-btn size="x-small" color="info" variant="elevated" @click="expandedOrderId = expandedOrderId === item.id ? null : item.id">Info</v-btn>
                <v-btn
                  v-if="item.order_status_name?.toLowerCase() === 'pending_invoice'"
                  size="x-small" color="success" variant="elevated" class="ml-1"
                  @click="paySingleOrder(item)" :loading="actionLoading === item.id"
                >
                  <v-icon size="small" class="mr-1">mdi-cash</v-icon> Pay
                </v-btn>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Expanded Order Details (below table) -->
        <v-card v-if="expandedOrderId && expandedOrder" flat class="mt-2 bg-grey-lighten-4">
          <v-card-title class="text-subtitle-2 py-2 bg-grey-lighten-3 d-flex align-center">
            <v-icon size="small" class="mr-2">mdi-format-list-bulleted</v-icon>
            Order #{{ expandedOrderId }} - {{ t.orders.services }} ({{ expandedOrder.order_client_services?.length || 0 }})
            <v-spacer></v-spacer>
            <v-btn size="x-small" variant="text" @click="expandedOrderId = null"><v-icon>mdi-close</v-icon></v-btn>
          </v-card-title>
          <v-card-text class="pa-2" v-if="expandedOrder.order_client_services?.length > 0">
            <v-table density="compact">
              <thead>
                <tr class="bg-grey-lighten-2">
                  <th>CS ID</th>
                  <th>{{ t.orders.service }}</th>
                  <th>{{ t.orders.service_type }}</th>
                  <th>{{ t.orders.client }}</th>
                  <th>{{ t.orders.cs_status }}</th>
                  <th>IMSI</th>
                  <th>Serial</th>
                  <th>MSISDN</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="ocs in expandedOrder.order_client_services" :key="ocs.id">
                  <td>
                    <v-btn size="x-small" color="primary" variant="text" @click="goToClientService(ocs.client_service_id)">#{{ ocs.client_service_id }}</v-btn>
                  </td>
                  <td>{{ ocs.service_name }}</td>
                  <td>{{ ocs.service_type_name }}</td>
                  <td>{{ ocs.client_name }}</td>
                  <td>{{ ocs.client_service_status_name }}</td>
                  <td>{{ ocs.imsi || '-' }}</td>
                  <td>{{ ocs.imsi_serial || '-' }}</td>
                  <td>{{ ocs.msisdn || '-' }}</td>
                </tr>
              </tbody>
            </v-table>
          </v-card-text>
        </v-card>

        <!-- Pagination -->
        <v-pagination
          v-if="pagination.total_pages > 1"
          v-model="pagination.page"
          :length="pagination.total_pages"
          :total-visible="7"
          class="mt-4"
          @update:model-value="loadOrders"
        ></v-pagination>

        </template><!-- end orders tab -->
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getOrdersList, getOrderStatuses, payOrderApi, deleteOrderApi, provisionOrderApi, deliverOrderApi, markApiReceivedApi, confirmOrdersPaymentApi, payOrdersOnlineApi, getPaymentHistoryApi, exportOrdersList } from "@/composables/useGetOrdersAPI";
import { getGoodsOrdersForDistributor } from "@/composables/useGoodsOrdersAPI";
import { getClientTree } from "@/composables/useGetClientsAPI";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";
import NavBar from "@/components/NavBar.vue";
import FulfillGoodsOrderDialog from "@/components/FulfillGoodsOrderDialog.vue";
import ClientHierarchySelect from "@/components/order/ClientHierarchySelect.vue";
import ServiceTypeButtonFilter from "@/components/ServiceTypeButtonFilter.vue";

export default defineComponent({
  name: 'OrdersList',
  components: { NavBar, FulfillGoodsOrderDialog, ClientHierarchySelect, ServiceTypeButtonFilter },
  data() {
    return {
      loading: false,
      actionLoading: null,
      token: '',
      user: null,

      // Client Filter
      clientsList: [],
      selectedClientId: null,
      loadingClients: false,

      // Filters
      serviceTypeFilter: 'all',
      includeSubclients: false,
      searchImsi: '',
      searchMsisdn: '',
      selectedStatusId: 'all',
      selectedPeriod: 'today',
      customPeriodStart: null,
      customPeriodEnd: null,
      exportLoading: false,
      showSummaryTotals: false,
      showSummaryDetailed: false,

      // Data
      orders: [],
      orderStatuses: [],
      summary: null,
      availableOrderTypes: [],

      // Pagination
      pagination: {
        page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0
      },

      // Delete confirmation dialog
      deleteDialog: false,
      orderToDelete: null,

      // Payment dialog
      showPaymentDialog: false,
      paymentAmount: null,
      paymentNotes: '',
      paymentLoading: false,
      payOnlineLoading: false,

      // Snackbar
      showSnackbar: false,
      snackbarMessage: '',
      snackbarColor: 'success',

      // Payment History
      paymentBatches: [],
      loadingBatches: false,
      batchesPagination: {
        page: 1,
        per_page: 20,
        total_count: 0,
        total_pages: 0
      },

      // Goods Orders for Distributor
      isDistributor: false,
      goodsOrders: [],
      loadingGoodsOrders: false,
      orderTypeTab: 'all',
      showFulfillDialog: false,
      selectedGoodsOrder: null,
      expandedOrderId: null,
      selectedOrders: []
    };
  },
  computed: {
    t() {
      return tRef.value;
    },
    expandedOrder() {
      if (!this.expandedOrderId) return null;
      return this.orders.find(o => o.id === this.expandedOrderId) || null;
    },
    orderStatusOptions() {
      return [
        { id: 'all', name: this.t.common.all },
        ...this.orderStatuses
      ];
    },
    periodOptions() {
      return [
        { value: 'all', label: this.t.orders.period_all },
        { value: 'today', label: this.t.orders.period_today },
        { value: 'current_month', label: this.t.orders.period_current_month },
        { value: 'last_month', label: this.t.orders.period_last_month },
        { value: 'before_last_month', label: this.t.orders.period_before_last_month },
        { value: 'custom', label: this.t.orders.period_custom || 'Custom' }
      ];
    },
    selectAll: {
      get() { return this.orders.length > 0 && this.selectedOrders.length === this.orders.length; },
      set(val) { this.selectedOrders = val ? this.orders.map(o => o.id) : []; }
    },
    pendingInvoiceOrders() {
      return this.orders.filter(o => o.order_status_name?.toLowerCase() === 'pending_invoice');
    },
    pendingInvoiceTotal() {
      return this.pendingInvoiceOrders.reduce((sum, o) => sum + (parseFloat(o.total_cost) || 0), 0);
    },
    selectedPendingInvoiceOrders() {
      const pool = this.selectedOrders.length > 0
        ? this.orders.filter(o => this.selectedOrders.includes(o.id))
        : this.orders;
      return pool.filter(o => o.order_status_name?.toLowerCase() === 'pending_invoice');
    },
    selectedPendingInvoiceTotal() {
      return this.selectedPendingInvoiceOrders.reduce((sum, o) => sum + (parseFloat(o.total_cost) || 0), 0);
    },
    canConfirmPayment() {
      if (!this.user) return false;
      const roles = store.getters.getRoles || [];
      return this.user.is_admin || this.user.admin || this.user.is_financial || roles.includes('approve_payment');
    },
    pendingGoodsOrders() {
      return this.goodsOrders.filter(o =>
        ['paid', 'pending_invoice', 'processing'].includes(o.order_status?.toLowerCase())
      );
    },
    goodsOrderHeaders() {
      return [
        { title: 'ID', key: 'id', width: '70px' },
        { title: 'Data', key: 'created_at', width: '130px' },
        { title: 'Client', key: 'requester', sortable: false, width: '180px' },
        { title: 'Detalii', key: 'details', sortable: false },
        { title: 'Livrare', key: 'delivery', sortable: false },
        { title: 'Total', key: 'total', width: '100px' },
        { title: 'Status', key: 'status', width: '130px' },
        { title: 'Actiuni', key: 'actions', width: '140px', sortable: false }
      ];
    },
    hasSubclients() {
      return this.clientsList && this.clientsList.length > 1;
    },
    batchHeaders() {
      return [
        { title: '#', key: 'id', width: '70px' },
        { title: this.t.orders.created_at, key: 'created_at', width: '140px' },
        { title: this.t.orders?.client || 'Client', key: 'client_name' },
        { title: this.t.orders.amount_received, key: 'amount_received', width: '100px' },
        { title: this.t.orders.amount_applied, key: 'amount_applied', width: '100px' },
        { title: this.t.orders.amount_credit, key: 'amount_credit', width: '80px' },
        { title: this.t.orders.order_count, key: 'orders_count', width: '100px', sortable: false },
        { title: this.t.orders.payment_method || 'Method', key: 'payment_method', width: '90px' },
        { title: this.t.orders.payment_notes, key: 'payment_notes' },
        { title: this.t.orders.confirmed_by, key: 'confirmed_by_user', width: '150px' },
        { title: '', key: 'data-table-expand', width: '50px' }
      ];
    }
  },
  methods: {
    async loadOrders() {
      this.loading = true;
      try {
        const params = {
          include_subclients: this.includeSubclients,
          page: this.pagination.page,
          per_page: this.pagination.per_page
        };

        // Add client filter if selected
        if (this.selectedClientId) {
          params.client_id = this.selectedClientId;
        }

        if (this.searchImsi && this.searchImsi.length >= 3) {
          params.search_imsi = this.searchImsi;
        }
        if (this.searchMsisdn && this.searchMsisdn.length >= 3) {
          params.search_msisdn = this.searchMsisdn;
        }
        if (this.selectedStatusId && this.selectedStatusId !== 'all') {
          params.order_status_id = this.selectedStatusId;
        }
        if (this.selectedPeriod && this.selectedPeriod !== 'all') {
          params.period = this.selectedPeriod;
          if (this.selectedPeriod === 'custom') {
            if (this.customPeriodStart) params.period_start = this.customPeriodStart;
            if (this.customPeriodEnd) params.period_end = this.customPeriodEnd;
          }
        }
        if (this.orderTypeTab && this.orderTypeTab !== 'all') {
          params.order_type = this.orderTypeTab;
        }
        if (this.serviceTypeFilter !== 'all') {
          params.service_type_id = this.serviceTypeFilter;
        }
        if (this.showSummaryTotals) {
          params.include_summary = 'true';
        }

        const response = await getOrdersList(this.token, params);

        if (response.error) {
          log("Error loading orders: " + response.message);
          this.orders = [];
          this.summary = null;
        } else {
          this.orders = response.orders || [];
          this.pagination = response.pagination || this.pagination;
          this.summary = response.summary || null;

          if (response.available_order_types &&
              JSON.stringify(this.availableOrderTypes) !== JSON.stringify(response.available_order_types)) {
            this.availableOrderTypes = response.available_order_types;
          }
        }
      } catch (err) {
        log("Exception loading orders:");
        log(err);
        this.orders = [];
        this.summary = null;
      } finally {
        this.loading = false;
      }
    },

    async loadOrderStatuses() {
      try {
        const response = await getOrderStatuses(this.token);
        if (!response.error) {
          this.orderStatuses = response.statuses || [];
        }
      } catch (err) {
        log("Error loading order statuses:");
        log(err);
      }
    },

    onPageChange(page) {
      this.pagination.page = page;
      this.loadOrders();
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    async exportExcel() {
      this.exportLoading = true;
      try {
        const params = {
          include_subclients: this.includeSubclients,
        };
        if (this.selectedClientId) params.client_id = this.selectedClientId;
        if (this.searchImsi && this.searchImsi.length >= 3) params.search_imsi = this.searchImsi;
        if (this.searchMsisdn && this.searchMsisdn.length >= 3) params.search_msisdn = this.searchMsisdn;
        if (this.selectedStatusId && this.selectedStatusId !== 'all') params.order_status_id = this.selectedStatusId;
        if (this.selectedPeriod && this.selectedPeriod !== 'all') {
          params.period = this.selectedPeriod;
          if (this.selectedPeriod === 'custom') {
            if (this.customPeriodStart) params.period_start = this.customPeriodStart;
            if (this.customPeriodEnd) params.period_end = this.customPeriodEnd;
          }
        }
        if (this.serviceTypeFilter !== 'all') {
          params.service_type_id = this.serviceTypeFilter;
        }

        const blob = await exportOrdersList(this.token, params);
        if (blob && !blob.error) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `orders_export_${new Date().toISOString().split('T')[0]}.xlsx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }
      } catch (err) {
        log("Error exporting orders: " + err);
      } finally {
        this.exportLoading = false;
      }
    },

    formatCurrency(amount, currency = 'RON') {
      if (!amount && amount !== 0) return '-';
      const currencyMap = { 'Lei': 'RON', 'Euro': 'EUR', 'Dollar': 'USD', 'lei': 'RON', 'euro': 'EUR', 'EUR': 'EUR', 'RON': 'RON', 'USD': 'USD' };
      const currencyCode = currencyMap[currency] || 'RON';
      try {
        return new Intl.NumberFormat('ro-RO', {
          style: 'currency',
          currency: currencyCode
        }).format(amount);
      } catch (e) {
        return `${parseFloat(amount).toFixed(2)} ${currency || 'RON'}`;
      }
    },

    getStatusColor(status) {
      const colors = {
        'completed': 'success',
        'paid': 'success',
        'pending': 'warning',
        'pending_payment': 'warning',
        'pending_invoice': 'blue',
        'cancelled': 'error',
        'failed': 'error',
        'refunded': 'info'
      };
      return colors[status?.toLowerCase()] || 'grey';
    },

    getCsStatusColor(status) {
      const colors = {
        'active': 'success',
        'pending_payment': 'warning',
        'suspended': 'orange',
        'expired': 'error',
        'terminated': 'grey'
      };
      return colors[status?.toLowerCase()] || 'grey';
    },

    getSimStatusColor(status) {
      const colors = {
        'active': 'success',
        'not_provisioned': 'info',
        'suspended': 'orange',
        'expired': 'error',
        'deleted': 'grey'
      };
      return colors[status?.toLowerCase()] || 'grey';
    },

    // Check if order has imsi_msisdn service type
    hasImsiMsisdnService(order) {
      if (!order.order_client_services) return false;
      return order.order_client_services.some(
        ocs => ocs.service_type_name?.toLowerCase() === 'imsi_msisdn'
      );
    },

    needsPhysicalDelivery(order) {
      const dt = order.delivery_type_name?.toLowerCase();
      if (!dt || dt === 'electronic') return false;
      return true; // courier, personal_delivery, personal_pickup
    },

    goToClientService(csId) {
      this.$router.push({ name: 'clientServices', query: { cs_id: csId } });
    },

    onOrderTypeChange(val) {
      if (val === 'payment_history') {
        this.loadPaymentHistory();
      } else {
        if (this.loading) return;
        this.pagination.page = 1;
        this.loadOrders();
      }
    },

    async loadPaymentHistory() {
      this.loadingBatches = true;
      try {
        const params = {
          include_subclients: this.includeSubclients,
          page: this.batchesPagination.page,
          per_page: this.batchesPagination.per_page
        };
        if (this.selectedClientId) {
          params.client_id = this.selectedClientId;
        }

        const response = await getPaymentHistoryApi(this.token, params);
        if (response.error) {
          log("Error loading payment history: " + response.message);
          this.paymentBatches = [];
        } else {
          this.paymentBatches = response.batches || [];
          this.batchesPagination = response.pagination || this.batchesPagination;
        }
      } catch (err) {
        log("Exception loading payment history:");
        log(err);
        this.paymentBatches = [];
      } finally {
        this.loadingBatches = false;
      }
    },

    // ========== PAYMENT METHODS ==========

    openPaymentDialog() {
      if (this.selectedPendingInvoiceOrders.length === 0) {
        this.showSnackbarMsg(this.t.orders.no_pending_orders, 'warning');
        return;
      }
      this.paymentAmount = this.selectedPendingInvoiceTotal;
      this.paymentNotes = '';
      this.showPaymentDialog = true;
    },

    async submitConfirmPayment() {
      this.paymentLoading = true;
      try {
        const orderIds = this.selectedPendingInvoiceOrders.map(o => o.id);
        const data = {
          order_ids: orderIds,
          payment_notes: this.paymentNotes
        };
        if (this.paymentAmount !== null && this.paymentAmount !== '') {
          data.payment_amount = parseFloat(this.paymentAmount);
        }

        const response = await confirmOrdersPaymentApi(this.token, data);
        if (response.error) {
          this.showSnackbarMsg(response.message || 'Error', 'error');
        } else {
          this.showSnackbarMsg(
            `${this.t.orders.payment_success}: ${response.orders_paid_count}/${response.orders_total_count} orders, ${this.formatCurrency(response.amount_applied)} applied` +
            (response.amount_credit > 0 ? `, ${this.formatCurrency(response.amount_credit)} credit` : ''),
            'success'
          );
          this.showPaymentDialog = false;
          this.selectedOrders = [];
          await this.loadOrders();
        }
      } catch (err) {
        log("Error confirming payment:");
        log(err);
        this.showSnackbarMsg('Error confirming payment', 'error');
      } finally {
        this.paymentLoading = false;
      }
    },

    async payOnline() {
      if (this.selectedPendingInvoiceOrders.length === 0) {
        this.showSnackbarMsg(this.t.orders.no_pending_orders, 'warning');
        return;
      }
      this.payOnlineLoading = true;
      try {
        const orderIds = this.selectedPendingInvoiceOrders.map(o => o.id);
        const response = await payOrdersOnlineApi(this.token, {
          order_ids: orderIds
        });

        if (response.error) {
          this.showSnackbarMsg(response.message || 'Error', 'error');
        } else if (response.formUrl) {
          this.showSnackbarMsg(this.t.orders.payment_initiated, 'info');
          const payWindow = window.open(response.formUrl, 'bt_payment', 'width=800,height=600');
          // Poll for window close
          const pollTimer = setInterval(() => {
            if (payWindow && payWindow.closed) {
              clearInterval(pollTimer);
              this.selectedOrders = [];
              this.loadOrders();
            }
          }, 1000);
        }
      } catch (err) {
        log("Error initiating online payment:");
        log(err);
        this.showSnackbarMsg('Error initiating online payment', 'error');
      } finally {
        this.payOnlineLoading = false;
      }
    },

    async paySingleOrder(order) {
      this.actionLoading = order.id;
      try {
        const response = await payOrdersOnlineApi(this.token, {
          order_ids: [order.id]
        });
        if (response.error) {
          this.showSnackbarMsg(response.message || 'Error', 'error');
        } else if (response.formUrl) {
          this.showSnackbarMsg(this.t.orders.payment_initiated || 'Payment initiated', 'info');
          const payWindow = window.open(response.formUrl, 'bt_payment', 'width=800,height=600');
          const pollTimer = setInterval(() => {
            if (payWindow && payWindow.closed) {
              clearInterval(pollTimer);
              this.loadOrders();
            }
          }, 1000);
        }
      } catch (err) {
        log("Error initiating single order payment:");
        log(err);
        this.showSnackbarMsg('Error initiating payment', 'error');
      } finally {
        this.actionLoading = null;
      }
    },

    showSnackbarMsg(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
    },

    // Action: Pay Order (single)
    async payOrder(order) {
      this.actionLoading = order.id;
      try {
        const response = await payOrderApi(this.token, order.id);
        if (response.error) {
          alert(response.message || 'Error processing payment');
        } else if (response.btUrl) {
          // Redirect to payment page
          window.open(response.btUrl, '_blank');
        } else {
          // Payment successful, reload orders
          await this.loadOrders();
        }
      } catch (err) {
        log("Error paying order:");
        log(err);
        alert('Error processing payment');
      } finally {
        this.actionLoading = null;
      }
    },

    // Action: Delete Order (show confirmation)
    confirmDeleteOrder(order) {
      this.orderToDelete = order;
      if (confirm(this.t.orders.confirm_delete_order + ' #' + order.id + '?')) {
        this.deleteOrder();
      }
    },

    // Action: Delete Order
    async deleteOrder() {
      if (!this.orderToDelete) return;

      this.actionLoading = this.orderToDelete.id;
      try {
        const response = await deleteOrderApi(this.token, this.orderToDelete.id);
        if (response.error) {
          alert(response.message || 'Error deleting order');
        } else {
          await this.loadOrders();
        }
      } catch (err) {
        log("Error deleting order:");
        log(err);
        alert('Error deleting order');
      } finally {
        this.actionLoading = null;
        this.orderToDelete = null;
      }
    },

    // Action: Provision Order
    async provisionOrder(order) {
      this.actionLoading = order.id;
      try {
        const response = await provisionOrderApi(this.token, order.id);
        if (response.error) {
          alert(response.message || 'Error provisioning order');
        } else {
          await this.loadOrders();
        }
      } catch (err) {
        log("Error provisioning order:");
        log(err);
        alert('Error provisioning order');
      } finally {
        this.actionLoading = null;
      }
    },

    // Action: Deliver Order
    async deliverOrder(order) {
      this.actionLoading = order.id;
      try {
        const response = await deliverOrderApi(this.token, order.id);
        if (response.error) {
          alert(response.message || 'Error delivering order');
        } else {
          await this.loadOrders();
        }
      } catch (err) {
        log("Error delivering order:");
        log(err);
        alert('Error delivering order');
      } finally {
        this.actionLoading = null;
      }
    },

    // Action: Mark API Received
    async markApiReceived(order) {
      this.actionLoading = order.id;
      try {
        const response = await markApiReceivedApi(this.token, order.id);
        if (response.error) {
          alert(response.message || 'Error marking API received');
        } else {
          await this.loadOrders();
        }
      } catch (err) {
        log("Error marking API received:");
        log(err);
        alert('Error marking API received');
      } finally {
        this.actionLoading = null;
      }
    },

    // ========== GOODS ORDERS METHODS ==========

    async loadGoodsOrders() {
      if (!this.isDistributor) return;

      this.loadingGoodsOrders = true;
      try {
        const response = await getGoodsOrdersForDistributor(this.token);
        if (response.error) {
          log("Error loading goods orders: " + response.message);
          this.goodsOrders = [];
        } else {
          this.goodsOrders = response.orders || [];
        }
      } catch (err) {
        log("Exception loading goods orders:");
        log(err);
        this.goodsOrders = [];
      } finally {
        this.loadingGoodsOrders = false;
      }
    },

    canFulfillGoodsOrder(order) {
      return ['paid', 'pending_invoice', 'processing'].includes(order.order_status?.toLowerCase());
    },

    openFulfillDialog(order) {
      this.selectedGoodsOrder = order;
      this.showFulfillDialog = true;
    },

    closeFulfillDialog() {
      this.showFulfillDialog = false;
      this.selectedGoodsOrder = null;
    },

    onOrderFulfilled(response) {
      log("Order fulfilled:");
      log(response);
      this.closeFulfillDialog();
      this.loadGoodsOrders();
    },

    getGoodsOrderStatusColor(status) {
      const colors = {
        'pending_payment': 'warning',
        'pending_invoice': 'info',
        'paid': 'success',
        'processing': 'primary',
        'shipped': 'info',
        'delivered': 'success',
        'return_pending': 'orange',
        'returned': 'grey',
        'lost_in_transit': 'error',
        'refunded': 'purple',
        'cancelled': 'grey'
      };
      return colors[status?.toLowerCase()] || 'grey';
    },

    // ========== CLIENT HIERARCHY METHODS ==========

    onClientChange(data) {
      log("Client changed:", data);
      this.selectedClientId = data?.clientId || null;
    },

    // Build flat list from client tree with indentation
    buildClientsList(clientsTree) {
      const list = [];
      const addClientsRecursively = (clients, level = 0) => {
        if (!clients || clients.length === 0) return;
        clients.forEach(client => {
          const indent = level > 0 ? '\u2014'.repeat(level) + ' ' : '';
          list.push({
            id: client.client_id || client.id,
            name: client.name,
            displayName: indent + client.name,
            level: level
          });
          if (client.children && client.children.length > 0) {
            addClientsRecursively(client.children, level + 1);
          }
        });
      };
      addClientsRecursively(clientsTree);
      return list;
    },

    async loadClients() {
      try {
        this.loadingClients = true;
        this.user = store.getters.getUser;
        if (!this.user || !this.user.client_id) {
          log("No user or client_id found");
          return;
        }
        const response = await getClientTree(this.token, this.user.client_id);
        if (!response.error && response.clients_list) {
          this.clientsList = this.buildClientsList(response.clients_list);
        }
      } catch (error) {
        log("Error loading clients:");
        log(error);
      } finally {
        this.loadingClients = false;
      }
    }
  },
  watch: {
    serviceTypeFilter(newVal, oldVal) {
      if (oldVal !== undefined && oldVal !== newVal) {
        this.pagination.page = 1;
        this.loadOrders();
      }
    }
  },
  async mounted() {
    this.token = store.getters.getToken;

    // Check if current user's client is a distributor
    const clientData = store.getters.getClientData;
    this.isDistributor = clientData?.is_distributor === true ||
                         clientData?.is_distributor === 1 ||
                         clientData?.is_distributor === "true";

    // Load clients hierarchy for filter
    await this.loadClients();
    await this.loadOrderStatuses();

    // Load goods orders if distributor
    if (this.isDistributor) {
      await this.loadGoodsOrders();
    }
  }
});
</script>

<style scoped>
.v-table th {
  font-weight: 600 !important;
  white-space: nowrap;
}
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
}
.summary-card {
  background-color: rgba(33, 150, 243, 0.06) !important;
}
.detailed-client-row {
  background-color: rgba(33, 150, 243, 0.05);
  border-left: 3px solid rgb(var(--v-theme-primary));
}
.detailed-client-row:hover {
  background-color: rgba(33, 150, 243, 0.1);
}
.orders-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.orders-table th,
.orders-table td {
  padding: 4px 8px;
  border-bottom: 1px solid #e0e0e0;
  text-align: left;
}
.orders-table th {
  font-weight: 600 !important;
  white-space: nowrap;
  background-color: #f5f5f5;
}
.orders-table tbody tr:hover {
  background-color: #f5f5f5;
}
</style>
